import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useSelector } from "react-redux";
import { FiEdit2, FiTrash2, FiEye } from "react-icons/fi";

/* --- Button Components --- */
const Button = ({ children, variant = "primary", ...rest }) => {
  const base =
    "px-4 py-2 rounded-md font-medium text-sm transition-all duration-200";
  const styles =
    variant === "primary"
      ? "bg-[#065697] text-white hover:bg-[#054d84]"
      : "border border-[#065697] text-[#065697] hover:bg-[#E6EEF4]";
  return (
    <button className={`${base} ${styles}`} {...rest}>
      {children}
    </button>
  );
};

/* --- Modal Wrapper --- */
const Modal = ({ open, title, onClose, children, footer }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-2">
      <div className="bg-white rounded-xl w-full max-w-md shadow-lg">
        <div className="flex justify-between items-center px-5 py-3 border-b">
          <h3 className="text-lg font-semibold">{title}</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            ✕
          </button>
        </div>
        <div className="p-5">{children}</div>
        <div className="flex justify-end gap-3 px-5 py-3 border-t">
          {footer}
        </div>
      </div>
    </div>
  );
};

/* --- Confirm Modal --- */
const ConfirmModal = ({ open, onClose, onConfirm, name }) => {
  if (!open) return null;
  return (
    <Modal
      open={open}
      title="Confirm Delete"
      onClose={onClose}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onConfirm}>
            Yes, Delete
          </Button>
        </>
      }
    >
      <p>
        Are you sure you want to delete IPO <b>{name}</b>?
      </p>
    </Modal>
  );
};

/* --- Add/Edit Modal --- */
const IpoModal = ({ open, onClose, onSave, editData }) => {
  const [form, setForm] = useState({
    name: "",
    startDate: "",
    endDate: "",
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (editData) {
      setForm({
        name: editData.name || "",
        startDate: editData.startDate
          ? editData.startDate.split("T")[0]
          : "",
        endDate: editData.endDate
          ? editData.endDate.split("T")[0]
          : "",
      });
    } else {
      setForm({ name: "", startDate: "", endDate: "" });
    }
  }, [editData, open]);

  const validate = () => {
    const e = {};
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const start = form.startDate ? new Date(form.startDate) : null;
    const end = form.endDate ? new Date(form.endDate) : null;

    // Required fields
    if (!form.name.trim()) e.name = "Name is required";
    if (!form.startDate) e.startDate = "Start date is required";
    if (!form.endDate) e.endDate = "End date is required";

    // Must be at least 1 day after start date
    if (start && end) {
      const minEnd = new Date(start);
      minEnd.setDate(start.getDate() + 1);

      if (end < minEnd) {
        e.endDate = "End date must be at least one day after start date";
      }
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    onSave(form);
  };

  const handleClose = () => {
    setForm({ name: "", startDate: "", endDate: "" });
    setErrors({});
    onClose();
  };

  // dynamically set min end date based on start date
  const minEndDate =
    form.startDate &&
    (() => {
      const min = new Date(form.startDate);
      min.setDate(min.getDate() + 1);
      return min.toISOString().split("T")[0];
    })();

  return (
    <Modal
      open={open}
      title={editData ? "Edit IPO" : "Create IPO"}
      onClose={handleClose}
      footer={
        <>
          <Button variant="outline" onClick={handleClose}>
            Close
          </Button>
          <Button variant="primary" onClick={handleSubmit}>
            {editData ? "Update" : "Create"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {/* IPO Name */}
        <div>
          <label className="block text-sm font-medium mb-1">IPO Name</label>
          <input
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Enter IPO name"
          />
          {errors.name && (
            <p className="text-red-500 text-xs mt-1">{errors.name}</p>
          )}
        </div>

        {/* Dates */}
        <div className="flex gap-3">
          <div className="w-1/2">
            <label className="block text-sm font-medium mb-1">Start Date</label>
            <input
              type="date"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              value={form.startDate}
              onChange={(e) =>
                setForm({ ...form, startDate: e.target.value, endDate: "" })
              }
            />
            {errors.startDate && (
              <p className="text-red-500 text-xs mt-1">{errors.startDate}</p>
            )}
          </div>

          <div className="w-1/2">
            <label className="block text-sm font-medium mb-1">End Date</label>
            <input
              type="date"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              value={form.endDate}
              min={minEndDate || new Date().toISOString().split("T")[0]} // ✅ UI enforcement
              onChange={(e) => setForm({ ...form, endDate: e.target.value })}
            />
            {errors.endDate && (
              <p className="text-red-500 text-xs mt-1">{errors.endDate}</p>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};




/* --- Main Component --- */
export default function ManageClientPage({handleIPONavigate}) {
  const { token } = useSelector((s) => s.login);
  const auth = useMemo(() => localStorage.getItem("token") || token, [token]);
  const base = `${process.env.REACT_APP_BASE_URL}/${process.env.REACT_APP_API_TYPE}`;

  const [ipos, setIpos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [deleteData, setDeleteData] = useState(null);

  // pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchIpos = async (pageNum = 1) => {
    setLoading(true);
    try {
      const res = await axios.get(`${base}/ipoManageClient/ipo?page=${pageNum}`, {
        headers: { Authorization: `Bearer ${auth}` },
      });

      const data = res?.data?.data || {};
      setIpos(data.list || []);
      setTotalPages(data.totalPages || 1);
      setPage(data.currentPage || 1);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIpos(page);
  }, [page]);

  const handleSave = async (data) => {
    try {
      if (editData) {
        await axios.put(`${base}/ipoManageClient/ipo/${editData.id}`, data, {
          headers: { Authorization: `Bearer ${auth}` },
        });
      } else {
        await axios.post(`${base}/ipoManageClient/ipo`, data, {
          headers: { Authorization: `Bearer ${auth}` },
        });
      }
      setModalOpen(false);
      setEditData(null);
      fetchIpos(page);
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${base}/ipoManageClient/ipo/${deleteData.id}`, {
        headers: { Authorization: `Bearer ${auth}` },
      });
      setDeleteData(null);
      fetchIpos(page);
    } catch (err) {
      console.error(err);
    }
  };

  const filteredIpos =
    ipos?.filter((i) => i.name.toLowerCase().includes(search.toLowerCase())) ||
    [];

  return (
    <div className="w-full flex justify-center min-h-screen py-10 bg-gray-50">
      <div className="w-[100%]  rounded-xl  p-6">
        <div className="flex bg-white justify-between items-center mb-6 p-4 rounded-xl">
          <h2 className="text-lg font-semibold">IPO Management</h2>
        </div>
 
        {/* Search Bar */}
        <div className="flex mb-6 justify-between">
          <div className="w-[60%] flex">
            <input
              type="text"
              placeholder="Search by IPO name"
              className="border border-gray-300 rounded-l-md px-3 py-2 w-full max-w-sm"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <button
              className="bg-[#065697] text-white px-4 py-2 rounded-r-md"
              onClick={() => fetchIpos(1)}
            >
              Search
            </button>
          </div>
            <Button onClick={() => setModalOpen(true)}>Add New IPO</Button>
 
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border border-gray-200 rounded-lg text-sm">
            <thead className="bg-[#F8FAFC]">
              <tr className="text-[#065697] font-semibold text-left">
                <th className="border-b p-3 w-16">Sr. No.</th>
                <th className="border-b p-3">IPO Name</th>
                <th className="border-b p-3">Start Date</th>
                <th className="border-b p-3">End Date</th>
                <th className="border-b p-3 text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="5" className="text-center py-4">
                    Loading...
                  </td>
                </tr>
              ) : filteredIpos?.length > 0 ? (
                filteredIpos.map((ipo, idx) => (
                  <tr key={ipo.id} className="hover:bg-gray-50">
                    <td className="p-3 border-b">{idx + 1}</td>
                    <td className="p-3 border-b">{ipo.name}</td>
                    <td className="p-3 border-b">
                      {ipo.startDate?.split("T")[0]}
                    </td>
                    <td className="p-3 border-b">
                      {ipo.endDate?.split("T")[0]}
                    </td>
                    <td className="p-3 border-b text-center flex justify-center gap-3">
                      <button
                        onClick={()=>handleIPONavigate(ipo)}
                        className="text-[#065697] hover:text-blue-600"
                      >
                        <FiEye size={18} />
                      </button>
                      <button
                        onClick={() => {
                          setEditData(ipo);
                          setModalOpen(true);
                        }}
                        className="text-gray-600 hover:text-[#065697]"
                      >
                        <FiEdit2 size={18} />
                      </button>
                      <button
                        onClick={() => setDeleteData(ipo)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <FiTrash2 size={18} />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="text-center py-4 text-gray-500">
                    No IPOs found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-2 mt-6">
            <button
              onClick={() => setPage((p) => Math.max(p - 1, 1))}
              disabled={page === 1}
              className={`px-3 py-1 border rounded ${
                page === 1
                  ? "text-gray-400 border-gray-200"
                  : "text-[#065697] border-[#065697]"
              }`}
            >
              Prev
            </button>
            {[...Array(totalPages)].map((_, i) => (
              <button
                key={i}
                onClick={() => setPage(i + 1)}
                className={`px-3 py-1 border rounded ${
                  page === i + 1
                    ? "bg-[#065697] text-white"
                    : "text-[#065697] border-[#065697]"
                }`}
              >
                {i + 1}
              </button>
            ))}
            <button
              onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
              disabled={page === totalPages}
              className={`px-3 py-1 border rounded ${
                page === totalPages
                  ? "text-gray-400 border-gray-200"
                  : "text-[#065697] border-[#065697]"
              }`}
            >
              Next
            </button>
          </div>
        )}

        {/* Modals */}
        <IpoModal
          open={modalOpen}
          onClose={() => {
            setModalOpen(false);
            setEditData(null);
          }}
          onSave={handleSave}
          editData={editData}
        />
        <ConfirmModal
          open={!!deleteData}
          onClose={() => setDeleteData(null)}
          onConfirm={handleDelete}
          name={deleteData?.name}
        />
      </div>
    </div>
  );
}
